import React, { Fragment, useState } from 'react';
import type { ISnowflakeAccount } from '../../interfaces/ISnowflakeAccountManagement';
import Button from '../controls/Button';
import { TrashIcon, PencilIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import SnowflakeIcon from '../../assets/images/icons/Snowflake_icon.svg';
import { classNames } from '../../utilities/styleUtils';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';

interface SnowflakeAccountCardProps {
  account: ISnowflakeAccount;
  onDelete: (accountId: string) => void;
  onEdit?: (account: ISnowflakeAccount) => void;
}

/**
 * Display a single Snowflake account in a card format
 *
 * Features:
 * - Display account identifier prominently
 * - Display username
 * - Edit button
 * - Delete button with confirmation
 * - Snowflake icon
 *
 * @param account - The account data
 * @param onDelete - Delete callback
 * @param onEdit - Edit callback
 */
export function SnowflakeAccountCard({ account, onDelete, onEdit }: SnowflakeAccountCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete(account.id.toString());
    setShowDeleteDialog(false);
  };

  return (
    <>
      <div
        className={classNames(
          'relative rounded-lg border bg-white p-4 shadow-sm transition-all duration-200',
          'hover:shadow-md hover:border-dataops-primary-light-blue/30',
          'border-gray-200',
        )}
      >
        {/* Snowflake Icon */}
        <div className="absolute top-6 right-6">
          <img src={SnowflakeIcon} alt="Snowflake" className="h-8 w-8 opacity-60" />
        </div>

        {/* Account Information - Horizontal Layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pr-16">
          {/* Left side: Account name and details */}
          <div className="flex-1 min-w-0">
            {/* Account Identifier */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{account.account}</h3>

            {/* Username, Warehouse, and Role - Grid with fixed columns */}
            <div className="grid grid-cols-3 gap-6 text-sm">
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-gray-500 text-xs uppercase tracking-wide">Username</span>
                <span className="font-medium text-gray-900 truncate" title={account.username}>
                  {account.username}
                </span>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-gray-500 text-xs uppercase tracking-wide">Warehouse</span>
                <span className="text-gray-900 truncate" title={account.warehouse ?? '—'}>
                  {account.warehouse ?? '—'}
                </span>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-gray-500 text-xs uppercase tracking-wide">Role</span>
                <span className="text-gray-900 truncate" title={account.role ?? '—'}>
                  {account.role ?? '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Right side: Action Buttons */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            {onEdit && (
              <Button
                intent="light"
                size="medium"
                onClick={() => {
                  onEdit(account);
                }}
                disabled={account.is_editable === false}
                className="text-dataops-primary-light-blue hover:text-dataops-primary-dark-blue hover:bg-blue-50"
              >
                <PencilIcon className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
            <Button
              intent="light"
              size="medium"
              onClick={handleDeleteClick}
              disabled={account.is_editable === false}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>

        {/* Non-editable message */}
        {account.is_editable === false && (
          <p className="mt-3 text-xs text-gray-500">This account cannot be edited or deleted.</p>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Transition show={showDeleteDialog} as={Fragment}>
        <Dialog as="div" className="relative z-30" onClose={setShowDeleteDialog}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Delete Snowflake Account
                        </DialogTitle>
                        <div className="absolute right-0 top-0 pr-4 pt-4">
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={() => {
                              setShowDeleteDialog(false);
                            }}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete the connection to account{' '}
                            <span className="font-semibold">{account.account}</span>?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-4">
                    <Button size="large" intent="danger" className="flex items-center" onClick={handleConfirmDelete}>
                      <TrashIcon className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                    <Button
                      size="large"
                      intent="light"
                      className="flex items-center"
                      onClick={() => {
                        setShowDeleteDialog(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
